import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Heart, Building2, ArrowRight, ArrowLeft, Check, AlertCircle,
  Briefcase, Target, Users, Globe, CheckCircle, Sparkles
} from 'lucide-react'
import { useAuth } from '../../App'

const STEPS = [
  { id: 'company', title: 'Company Info', icon: Building2 },
  { id: 'goals', title: 'Your Goals', icon: Target },
  { id: 'audience', title: 'Target Audience', icon: Users },
  { id: 'complete', title: 'Complete', icon: CheckCircle },
]

const INDUSTRIES = [
  { value: 'medtech', label: 'MedTech / Medical Devices' },
  { value: 'pharma', label: 'Pharmaceuticals' },
  { value: 'digital-health', label: 'Digital Health / HealthTech' },
  { value: 'wellness', label: 'Wellness / Supplements' },
  { value: 'healthcare-services', label: 'Healthcare Services' },
  { value: 'hospital', label: 'Hospital / Health System' },
  { value: 'biotech', label: 'Biotechnology' },
  { value: 'insurance', label: 'Health Insurance' },
  { value: 'other', label: 'Other Healthcare' },
]

const COMPANY_SIZES = [
  { value: 'startup', label: 'Startup (1-10 employees)' },
  { value: 'small', label: 'Small (11-50 employees)' },
  { value: 'medium', label: 'Medium (51-200 employees)' },
  { value: 'large', label: 'Large (201-1000 employees)' },
  { value: 'enterprise', label: 'Enterprise (1000+ employees)' },
]

const GOALS = [
  { value: 'brand-awareness', label: 'Brand Awareness', description: 'Increase visibility and recognition' },
  { value: 'product-launch', label: 'Product Launch', description: 'Introduce a new product or service' },
  { value: 'patient-education', label: 'Patient Education', description: 'Educate patients about conditions or treatments' },
  { value: 'hcp-engagement', label: 'HCP Engagement', description: 'Connect with healthcare professionals' },
  { value: 'clinical-trials', label: 'Clinical Trial Recruitment', description: 'Recruit participants for studies' },
  { value: 'thought-leadership', label: 'Thought Leadership', description: 'Establish authority in your field' },
]

const AUDIENCES = [
  { value: 'patients', label: 'Patients', description: 'People managing health conditions' },
  { value: 'caregivers', label: 'Caregivers', description: 'Family members and care providers' },
  { value: 'physicians', label: 'Physicians', description: 'Medical doctors and specialists' },
  { value: 'nurses', label: 'Nurses', description: 'Nursing professionals' },
  { value: 'pharmacists', label: 'Pharmacists', description: 'Pharmacy professionals' },
  { value: 'health-enthusiasts', label: 'Health Enthusiasts', description: 'General wellness audience' },
]

function BrandOnboarding() {
  const navigate = useNavigate()
  const { user, updateUserProfile } = useAuth()
  const [currentStep, setCurrentStep] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const [formData, setFormData] = useState({
    companyName: '',
    industry: '',
    companySize: '',
    website: '',
    jobTitle: '',
    goals: [],
    budget: '',
    targetAudiences: [],
    audienceConditions: '',
  })

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    setError('')
  }

  const handleMultiSelect = (field, value) => {
    setFormData(prev => {
      const current = prev[field]
      if (current.includes(value)) {
        return { ...prev, [field]: current.filter(v => v !== value) }
      }
      return { ...prev, [field]: [...current, value] }
    })
    setError('')
  }

  const validateStep = () => {
    switch (currentStep) {
      case 0: // Company Info
        if (!formData.companyName.trim()) {
          setError('Please enter your company name')
          return false
        }
        if (!formData.industry) {
          setError('Please select your industry')
          return false
        }
        if (!formData.jobTitle.trim()) {
          setError('Please enter your job title')
          return false
        }
        break
      case 1: // Goals
        if (formData.goals.length === 0) {
          setError('Please select at least one goal')
          return false
        }
        break
      case 2: // Audience
        if (formData.targetAudiences.length === 0) {
          setError('Please select at least one target audience')
          return false
        }
        break
    }
    return true
  }

  const handleNext = () => {
    if (!validateStep()) return

    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1)
    }
  }

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1)
    }
  }

  const handleComplete = async () => {
    setIsLoading(true)
    setError('')

    try {
      const response = await fetch('/api/onboarding', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          profile: formData,
          markComplete: true,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to complete onboarding')
      }

      updateUserProfile(data.user)
      navigate('/brand')
    } catch (err) {
      setError(err.message || 'Failed to complete onboarding. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const renderCompanyStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Tell us about your company</h2>
        <p className="mt-2 text-gray-600">This helps us match you with the right creators</p>
      </div>

      <div>
        <label htmlFor="companyName" className="label">Company name</label>
        <input
          id="companyName"
          name="companyName"
          type="text"
          value={formData.companyName}
          onChange={handleInputChange}
          className="input"
          placeholder="e.g., MedTech Innovations"
        />
      </div>

      <div>
        <label htmlFor="industry" className="label">Industry</label>
        <select
          id="industry"
          name="industry"
          value={formData.industry}
          onChange={handleInputChange}
          className="input"
        >
          <option value="">Select your industry</option>
          {INDUSTRIES.map(ind => (
            <option key={ind.value} value={ind.value}>{ind.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="companySize" className="label">Company size</label>
        <select
          id="companySize"
          name="companySize"
          value={formData.companySize}
          onChange={handleInputChange}
          className="input"
        >
          <option value="">Select company size</option>
          {COMPANY_SIZES.map(size => (
            <option key={size.value} value={size.value}>{size.label}</option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="jobTitle" className="label">Your job title</label>
        <input
          id="jobTitle"
          name="jobTitle"
          type="text"
          value={formData.jobTitle}
          onChange={handleInputChange}
          className="input"
          placeholder="e.g., VP of Marketing"
        />
      </div>

      <div>
        <label htmlFor="website" className="label">Company website (optional)</label>
        <div className="relative">
          <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            id="website"
            name="website"
            type="url"
            value={formData.website}
            onChange={handleInputChange}
            className="input pl-10"
            placeholder="https://yourcompany.com"
          />
        </div>
      </div>
    </div>
  )

  const renderGoalsStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900">What are your goals?</h2>
        <p className="mt-2 text-gray-600">Select all that apply to your marketing objectives</p>
      </div>

      <div className="grid gap-3">
        {GOALS.map(goal => (
          <button
            key={goal.value}
            type="button"
            onClick={() => handleMultiSelect('goals', goal.value)}
            className={`flex items-center p-4 rounded-xl border-2 transition-all text-left ${
              formData.goals.includes(goal.value)
                ? 'border-hf-teal-500 bg-hf-teal-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center mr-4 flex-shrink-0 ${
              formData.goals.includes(goal.value)
                ? 'border-hf-teal-500 bg-hf-teal-500'
                : 'border-gray-300'
            }`}>
              {formData.goals.includes(goal.value) && (
                <Check className="w-4 h-4 text-white" />
              )}
            </div>
            <div>
              <p className="font-medium text-gray-900">{goal.label}</p>
              <p className="text-sm text-gray-500">{goal.description}</p>
            </div>
          </button>
        ))}
      </div>

      <div>
        <label htmlFor="budget" className="label">Estimated annual influencer marketing budget</label>
        <select
          id="budget"
          name="budget"
          value={formData.budget}
          onChange={handleInputChange}
          className="input"
        >
          <option value="">Select budget range</option>
          <option value="under-25k">Under $25,000</option>
          <option value="25k-100k">$25,000 - $100,000</option>
          <option value="100k-500k">$100,000 - $500,000</option>
          <option value="500k-1m">$500,000 - $1,000,000</option>
          <option value="over-1m">Over $1,000,000</option>
        </select>
      </div>
    </div>
  )

  const renderAudienceStep = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Who do you want to reach?</h2>
        <p className="mt-2 text-gray-600">Select your primary target audiences</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {AUDIENCES.map(audience => (
          <button
            key={audience.value}
            type="button"
            onClick={() => handleMultiSelect('targetAudiences', audience.value)}
            className={`flex items-center p-4 rounded-xl border-2 transition-all text-left ${
              formData.targetAudiences.includes(audience.value)
                ? 'border-hf-teal-500 bg-hf-teal-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center mr-3 flex-shrink-0 ${
              formData.targetAudiences.includes(audience.value)
                ? 'border-hf-teal-500 bg-hf-teal-500'
                : 'border-gray-300'
            }`}>
              {formData.targetAudiences.includes(audience.value) && (
                <Check className="w-4 h-4 text-white" />
              )}
            </div>
            <div>
              <p className="font-medium text-gray-900">{audience.label}</p>
              <p className="text-xs text-gray-500">{audience.description}</p>
            </div>
          </button>
        ))}
      </div>

      <div>
        <label htmlFor="audienceConditions" className="label">
          Specific conditions or therapeutic areas (optional)
        </label>
        <textarea
          id="audienceConditions"
          name="audienceConditions"
          value={formData.audienceConditions}
          onChange={handleInputChange}
          className="input min-h-[100px]"
          placeholder="e.g., Diabetes management, cardiovascular health, oncology..."
        />
      </div>
    </div>
  )

  const renderCompleteStep = () => (
    <div className="text-center space-y-6">
      <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center">
        <CheckCircle className="w-10 h-10 text-green-600" />
      </div>

      <div>
        <h2 className="text-2xl font-bold text-gray-900">You're all set!</h2>
        <p className="mt-2 text-gray-600">
          Your brand profile is ready. Let's start connecting you with trusted healthcare creators.
        </p>
      </div>

      <div className="bg-hf-teal-50 rounded-xl p-6 text-left">
        <h3 className="font-semibold text-gray-900 mb-4">What's next?</h3>
        <ul className="space-y-3">
          <li className="flex items-start">
            <div className="w-6 h-6 bg-hf-teal-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
              <span className="text-hf-teal-600 text-sm font-medium">1</span>
            </div>
            <div>
              <p className="font-medium text-gray-900">Explore our creator network</p>
              <p className="text-sm text-gray-600">Browse verified healthcare influencers</p>
            </div>
          </li>
          <li className="flex items-start">
            <div className="w-6 h-6 bg-hf-teal-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
              <span className="text-hf-teal-600 text-sm font-medium">2</span>
            </div>
            <div>
              <p className="font-medium text-gray-900">Create your first campaign</p>
              <p className="text-sm text-gray-600">Use our guided brief builder</p>
            </div>
          </li>
          <li className="flex items-start">
            <div className="w-6 h-6 bg-hf-teal-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
              <span className="text-hf-teal-600 text-sm font-medium">3</span>
            </div>
            <div>
              <p className="font-medium text-gray-900">Get matched with creators</p>
              <p className="text-sm text-gray-600">Our AI finds the perfect fit</p>
            </div>
          </li>
        </ul>
      </div>

      <button
        onClick={handleComplete}
        disabled={isLoading}
        className="btn-primary w-full py-3"
      >
        {isLoading ? 'Setting up your account...' : 'Go to Dashboard'}
        {!isLoading && <ArrowRight className="ml-2 w-4 h-4" />}
      </button>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-gradient-to-br from-hf-teal-600 to-hf-teal-700 rounded-xl flex items-center justify-center">
              <Heart className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">SOFI Health</span>
          </div>
          <div className="flex items-center text-sm text-gray-500">
            <Sparkles className="w-4 h-4 mr-1 text-yellow-500" />
            Setting up your brand account
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    index < currentStep
                      ? 'bg-hf-teal-600 text-white'
                      : index === currentStep
                        ? 'bg-hf-teal-100 text-hf-teal-600 border-2 border-hf-teal-600'
                        : 'bg-gray-100 text-gray-400'
                  }`}>
                    {index < currentStep ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <step.icon className="w-5 h-5" />
                    )}
                  </div>
                  <span className={`mt-2 text-xs font-medium ${
                    index <= currentStep ? 'text-hf-teal-600' : 'text-gray-400'
                  }`}>
                    {step.title}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`flex-1 h-1 mx-2 rounded ${
                    index < currentStep ? 'bg-hf-teal-600' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 px-4 py-8">
        <div className="max-w-xl mx-auto">
          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start">
              <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          {currentStep === 0 && renderCompanyStep()}
          {currentStep === 1 && renderGoalsStep()}
          {currentStep === 2 && renderAudienceStep()}
          {currentStep === 3 && renderCompleteStep()}

          {/* Navigation Buttons */}
          {currentStep < 3 && (
            <div className="mt-8 flex justify-between">
              <button
                type="button"
                onClick={handleBack}
                disabled={currentStep === 0}
                className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
                  currentStep === 0
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </button>
              <button
                type="button"
                onClick={handleNext}
                className="btn-primary px-6 py-2"
              >
                Continue
                <ArrowRight className="ml-2 w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}

export default BrandOnboarding
